/**
 * useAutoMapping - React hook for automatic map updates based on character movement
 *
 * When enabled, this hook:
 * - Detects when the character jumps to a new system
 * - Adds new systems to the map automatically
 * - Creates connections between the previous and new systems
 * - Preserves connection direction (source → target)
 */

import { useCallback, useRef } from 'react';
import { useMapStore } from '../store/mapStore';
import { useAuthStore } from '../store/authStore';
import { apiUrl } from '../lib/config';
import useLocationPolling from './useLocationPolling';

interface UseAutoMappingOptions {
  /** Enable auto-mapping (default: false) */
  enabled?: boolean;
  /** Polling interval in milliseconds (default: 5000) */
  pollingInterval?: number;
  /** Callback when a new system is added */
  onSystemAdded?: (systemId: number) => void;
  /** Callback when a new connection is added */
  onConnectionAdded?: (sourceSystemId: number, targetSystemId: number) => void;
  /** WebSocket sendAction for broadcasting changes to other clients */
  sendAction?: (action: string, data: unknown) => void;
}

interface UseAutoMappingReturn {
  /** Current system ID */
  currentSystemId: number | null;
  /** Whether auto-mapping is active */
  isActive: boolean;
  /** Last error message */
  error: string | null;
  /** Manually trigger a location poll */
  pollNow: () => Promise<void>;
}

export function useAutoMapping(options: UseAutoMappingOptions = {}): UseAutoMappingReturn {
  const {
    enabled = false,
    pollingInterval = 5000,
    onSystemAdded,
    onConnectionAdded,
    sendAction,
  } = options;

  const { systems, addSystem, addConnection, currentMap } = useMapStore();
  const { character } = useAuthStore();

  // Track the last system we were in (on the map)
  const lastMappedSystemIdRef = useRef<string | null>(null);

  // Find the map system row ID for a given EVE system ID
  const findMapSystemId = useCallback(
    (eveSystemId: number): string | null => {
      const system = systems.find((s) => s.systemId === eveSystemId);
      return system?.id || null;
    },
    [systems]
  );

  // Send presence update to server for broadcasting to other clients
  const sendPresenceUpdate = useCallback(
    async (location: { systemId: number | null; shipTypeId?: number; isOnline: boolean }) => {
      if (!currentMap?.id || !character) {
        return;
      }

      try {
        await fetch(apiUrl(`/api/maps/${currentMap.id}/presence`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            characterId: character.id,
            characterName: character.name,
            systemId: location.systemId,
            shipTypeId: location.shipTypeId,
            isOnline: location.isOnline,
            lastSeen: Date.now(),
          }),
        });
      } catch (error) {
        console.error('Failed to send presence update:', error);
      }
    },
    [currentMap?.id, character]
  );

  // Check if a system is wormhole space (J-space)
  // Wormhole system IDs are in the range 31000000-31999999
  const isWormholeSystem = useCallback((systemId: number): boolean => {
    return systemId >= 31000000 && systemId <= 31999999;
  }, []);

  // Handle system changes
  const handleSystemChange = useCallback(
    async (previousSystemId: number | null, newSystemId: number) => {
      if (!currentMap || !enabled) {
        return;
      }

      // Check if the new system already exists on the map
      let newMapSystemId = findMapSystemId(newSystemId);

      // If the new system doesn't exist, only auto-add if it's a wormhole system (J-space)
      if (!newMapSystemId) {
        if (isWormholeSystem(newSystemId)) {
          const newSystem = await addSystem(newSystemId);
          if (newSystem) {
            newMapSystemId = newSystem.id;
            // Broadcast to other connected clients via WebSocket
            sendAction?.('ADD_SYSTEM', newSystem);
            onSystemAdded?.(newSystemId);
          } else {
            console.error('Failed to add system to map:', newSystemId);
            return;
          }
        } else {
          // K-space system not on map - don't auto-add, but keep tracking
          // for when we jump back to a mapped system
          return;
        }
      }

      // If we have a previous mapped system, create a connection
      if (lastMappedSystemIdRef.current && newMapSystemId) {
        // Check if connection already exists (in either direction)
        const existingConnection = useMapStore.getState().connections.find(
          (c) =>
            (c.sourceSystemId === lastMappedSystemIdRef.current &&
              c.targetSystemId === newMapSystemId) ||
            (c.sourceSystemId === newMapSystemId &&
              c.targetSystemId === lastMappedSystemIdRef.current)
        );

        if (!existingConnection) {
          // Create connection from previous system to new system
          const connection = await addConnection(
            lastMappedSystemIdRef.current,
            newMapSystemId
          );

          if (connection) {
            // Broadcast to other connected clients via WebSocket
            sendAction?.('ADD_CONNECTION', connection);
            if (previousSystemId) {
              onConnectionAdded?.(previousSystemId, newSystemId);
            }
          }
        }
      }

      // Update the last mapped system
      lastMappedSystemIdRef.current = newMapSystemId;
    },
    [currentMap, enabled, findMapSystemId, isWormholeSystem, addSystem, addConnection, onSystemAdded, onConnectionAdded, sendAction]
  );

  // Use location polling with system change and presence update callbacks
  const { location, isPolling, error, pollNow } = useLocationPolling({
    interval: pollingInterval,
    enabled: enabled && !!currentMap,
    onSystemChange: handleSystemChange,
    onLocationUpdate: sendPresenceUpdate,
  });

  // Initialize the last mapped system when we first get location
  const initializeRef = useRef(false);
  if (location?.systemId && !initializeRef.current && enabled) {
    const mapSystemId = findMapSystemId(location.systemId);
    if (mapSystemId) {
      lastMappedSystemIdRef.current = mapSystemId;
    }
    initializeRef.current = true;
  }

  return {
    currentSystemId: location?.systemId || null,
    isActive: isPolling && enabled,
    error,
    pollNow,
  };
}

export default useAutoMapping;
